import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

export interface AreaFolder {
  id: string;
  name: string;
  user_id: string;
  organization_id: string;
  is_expanded: boolean;
  created_at: string;
  updated_at: string;
  area_ids: string[];
}

export const useAreaFolders = () => {
  const { user } = useAuth();
  const [folders, setFolders] = useState<AreaFolder[]>([]);
  const [loading, setLoading] = useState(true);

  const loadFolders = useCallback(async () => {
    if (!user) {
      setFolders([]);
      setLoading(false);
      return;
    }

    try {
      // Load folders
      const { data: foldersData, error: foldersError } = await supabase
        .from('d2d_area_folders')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });

      if (foldersError) throw foldersError;

      if (!foldersData || foldersData.length === 0) {
        setFolders([]);
        setLoading(false);
        return;
      }

      // Load folder items
      const folderIds = foldersData.map(f => f.id);
      const { data: itemsData, error: itemsError } = await supabase
        .from('d2d_area_folder_items')
        .select('folder_id, area_id, sort_order')
        .in('folder_id', folderIds)
        .order('sort_order', { ascending: true });

      if (itemsError) throw itemsError;

      // Combine folders with their area IDs
      const foldersWithAreas: AreaFolder[] = foldersData.map(folder => ({
        ...folder,
        area_ids: (itemsData || [])
          .filter(item => item.folder_id === folder.id)
          .map(item => item.area_id)
      }));

      setFolders(foldersWithAreas);
    } catch (error) {
      console.error('Error loading folders:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  const createFolder = async (name: string, areaIds: string[]) => {
    if (!user) return;

    try {
      // Get user's organization_id
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .maybeSingle();

      // Create folder
      const { data: folder, error: folderError } = await supabase
        .from('d2d_area_folders')
        .insert({
          name,
          user_id: user.id,
          organization_id: profile?.organization_id || user.id
        })
        .select()
        .single();

      if (folderError) throw folderError;

      // Add areas to folder
      if (areaIds.length > 0) {
        const folderItems = areaIds.map((areaId, index) => ({
          folder_id: folder.id,
          area_id: areaId,
          sort_order: index
        }));

        const { error: itemsError } = await supabase
          .from('d2d_area_folder_items')
          .insert(folderItems);

        if (itemsError) throw itemsError;
      }

      toast.success(`Ordner "${name}" mit ${areaIds.length} Gebieten erstellt`);
      await loadFolders();
    } catch (error) {
      console.error('Error creating folder:', error);
      toast.error('Fehler beim Erstellen des Ordners');
      throw error;
    }
  };

  const deleteFolder = async (folderId: string, deleteAreas: boolean = false) => {
    if (!user) return;

    try {
      // If deleteAreas is true, first delete all areas in this folder
      if (deleteAreas) {
        const folder = folders.find(f => f.id === folderId);
        if (folder && folder.area_ids.length > 0) {
          // Delete each area using the cascade function
          for (const areaId of folder.area_ids) {
            const { error: areaError } = await supabase.rpc('delete_area_cascade', {
              area_id_param: areaId,
              user_id_param: user.id
            });
            
            if (areaError) {
              console.error('Error deleting area:', areaError);
              // Continue with other areas even if one fails
            }
          }
        }
      }

      // Then delete the folder
      const { error } = await supabase
        .from('d2d_area_folders')
        .delete()
        .eq('id', folderId)
        .eq('user_id', user.id);

      if (error) throw error;

      toast.success(deleteAreas ? 'Ordner und Gebiete gelöscht' : 'Ordner gelöscht');
      await loadFolders();
      
      return { areasDeleted: deleteAreas };
    } catch (error) {
      console.error('Error deleting folder:', error);
      toast.error('Fehler beim Löschen des Ordners');
    }
  };

  const toggleFolderExpanded = async (folderId: string, isExpanded: boolean) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('d2d_area_folders')
        .update({ is_expanded: isExpanded })
        .eq('id', folderId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Update local state immediately
      setFolders(prev => prev.map(f => 
        f.id === folderId ? { ...f, is_expanded: isExpanded } : f
      ));
    } catch (error) {
      console.error('Error toggling folder:', error);
    }
  };

  const removeAreaFromFolder = async (folderId: string, areaId: string) => {
    try {
      const { error } = await supabase
        .from('d2d_area_folder_items')
        .delete()
        .eq('folder_id', folderId)
        .eq('area_id', areaId);

      if (error) throw error;

      await loadFolders();
    } catch (error) {
      console.error('Error removing area from folder:', error);
      toast.error('Fehler beim Entfernen des Gebiets');
    }
  };

  // Get all area IDs that are in any folder
  const areasInFolders = folders.flatMap(f => f.area_ids);

  return {
    folders,
    loading,
    createFolder,
    deleteFolder,
    toggleFolderExpanded,
    removeAreaFromFolder,
    areasInFolders,
    refetch: loadFolders
  };
};
